import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import {
  Folder, FolderOpen as FolderOpenIcon, FileText, Trash2, RefreshCw,
  Save, Plus, Package, HardDrive, Terminal as TerminalIcon, Box,
} from 'lucide-react';
import {
  fetchAgentEnvironment, fetchEnvTree, fetchEnvFile, putEnvFile,
  deleteEnvPath, createEnvFolder, installEnvPackage, uninstallEnvPackage,
  rebuildEnvVenv,
} from '../api';
import './AgentEnvironment.css';

function fmtBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function langForPath(p) {
  const ext = (p || '').split('.').pop().toLowerCase();
  return {
    py: 'python', js: 'javascript', jsx: 'javascript', ts: 'typescript',
    tsx: 'typescript', json: 'json', md: 'markdown', sh: 'shell',
    yml: 'yaml', yaml: 'yaml', toml: 'ini', txt: 'plaintext',
    css: 'css', html: 'html',
  }[ext] || 'plaintext';
}

function TreeNode({ agentId, path, name, expanded, onToggle, onSelect, selected, depth, isVenv, refreshNonce }) {
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const isOpen = !!expanded[path];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchEnvTree(agentId, path);
      setChildren(r.entries || []);
    } catch (e) {
      setChildren([]);
    } finally {
      setLoading(false);
    }
  // refreshNonce in deps so Refresh button forces a re-fetch even for open folders
  }, [agentId, path, refreshNonce]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  return (
    <div className="env-tree-node">
      <button
        className={`env-tree-row ${selected === path ? 'selected' : ''} ${isVenv ? 'venv' : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => { onToggle(path); onSelect({ kind: 'dir', path }); }}
      >
        <span className="env-tree-icon">
          {isOpen ? <FolderOpenIcon size={14} /> : <Folder size={14} />}
        </span>
        <span className="env-tree-label">{name || '~'}</span>
      </button>
      {isOpen && (
        <div className="env-tree-children">
          {loading && <div className="env-tree-empty">loading…</div>}
          {!loading && children && children.length === 0 && (
            <div className="env-tree-empty" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>(empty)</div>
          )}
          {!loading && children && children.map(entry => {
            const childPath = path ? `${path}/${entry.name}` : entry.name;
            if (entry.kind === 'dir') {
              return (
                <TreeNode
                  key={childPath}
                  agentId={agentId}
                  path={childPath}
                  name={entry.name}
                  expanded={expanded}
                  onToggle={onToggle}
                  onSelect={onSelect}
                  selected={selected}
                  depth={depth + 1}
                  isVenv={entry.name === '.venv'}
                  refreshNonce={refreshNonce}
                />
              );
            }
            return (
              <button
                key={childPath}
                className={`env-tree-row file ${selected === childPath ? 'selected' : ''}`}
                style={{ paddingLeft: 8 + (depth + 1) * 14 }}
                onClick={() => onSelect({ kind: 'file', path: childPath, is_text: entry.is_text })}
              >
                <span className="env-tree-icon"><FileText size={14} /></span>
                <span className="env-tree-label">{entry.name}</span>
                <span className="env-tree-size">{fmtBytes(entry.size)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AgentEnvironment() {
  const { agentId } = useOutletContext();
  const [overview, setOverview] = useState(null);
  const [expanded, setExpanded] = useState({ '': true });
  const [selected, setSelected] = useState({ kind: 'dir', path: '' });
  const [fileBody, setFileBody] = useState(null); // {content, is_text, size}
  const [editorValue, setEditorValue] = useState('');
  const [dirty, setDirty] = useState(false);
  const [pkgName, setPkgName] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [treeNonce, setTreeNonce] = useState(0);

  const refreshOverview = useCallback(async (resetTree = false) => {
    try {
      const r = await fetchAgentEnvironment(agentId);
      setOverview(r);
    } catch (e) {
      setError(String(e.message || e));
    }
    if (resetTree) setTreeNonce(n => n + 1);
  }, [agentId]);

  useEffect(() => { refreshOverview(); }, [refreshOverview]);

  const onToggle = (path) => {
    setExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const loadFile = useCallback(async (path) => {
    setError('');
    setFileBody(null);
    setEditorValue('');
    setDirty(false);
    try {
      const r = await fetchEnvFile(agentId, path);
      setFileBody(r);
      setEditorValue(r.is_text ? (r.content ?? '') : '');
    } catch (e) {
      setError(String(e.message || e));
    }
  }, [agentId]);

  const onSelect = useCallback((sel) => {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    setSelected(sel);
    if (sel.kind === 'file') loadFile(sel.path);
    else { setFileBody(null); setEditorValue(''); setDirty(false); }
  }, [dirty, loadFile]);

  const save = async () => {
    if (selected.kind !== 'file') return;
    setBusy('save');
    setError('');
    try {
      await putEnvFile(agentId, selected.path, editorValue);
      setDirty(false);
      refreshOverview();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const removePath = async () => {
    if (!selected.path) return;
    if (!confirm(`Delete ${selected.path}?`)) return;
    setBusy('delete');
    try {
      await deleteEnvPath(agentId, selected.path);
      setSelected({ kind: 'dir', path: '' });
      setFileBody(null);
      setEditorValue('');
      setTreeNonce(n => n + 1);
      refreshOverview();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const newFolder = async () => {
    const name = prompt('Folder name (relative to selected dir):');
    if (!name) return;
    const base = selected.kind === 'dir' ? selected.path : selected.path.split('/').slice(0, -1).join('/');
    const path = base ? `${base}/${name}` : name;
    setBusy('mkdir');
    try {
      await createEnvFolder(agentId, path);
      setTreeNonce(n => n + 1);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const newFile = async () => {
    const name = prompt('File name (relative to selected dir):');
    if (!name) return;
    const base = selected.kind === 'dir' ? selected.path : selected.path.split('/').slice(0, -1).join('/');
    const path = base ? `${base}/${name}` : name;
    setBusy('mkfile');
    try {
      await putEnvFile(agentId, path, '');
      setTreeNonce(n => n + 1);
      setSelected({ kind: 'file', path, is_text: true });
      loadFile(path);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const installPkg = async () => {
    if (!pkgName.trim()) return;
    setBusy('install');
    setError('');
    try {
      const r = await installEnvPackage(agentId, pkgName.trim());
      if (!r.ok) setError(r.stderr || r.error || 'install failed');
      setPkgName('');
      refreshOverview();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const uninstallPkg = async (name) => {
    if (!confirm(`Uninstall ${name}?`)) return;
    setBusy(`uninstall:${name}`);
    try {
      await uninstallEnvPackage(agentId, name);
      refreshOverview();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const rebuild = async () => {
    if (!confirm('Wipe and recreate the venv? Installed packages will be lost.')) return;
    setBusy('rebuild');
    try {
      await rebuildEnvVenv(agentId);
      refreshOverview();
      setTreeNonce(n => n + 1);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(''); }
  };

  const showPackages = useMemo(() => {
    return selected.kind === 'dir' && (selected.path === '.venv' || selected.path.startsWith('.venv/'));
  }, [selected]);

  return (
    <div className="env-page">
      <div className="env-header">
        <div className="env-header-title">
          <TerminalIcon size={18} />
          <h2>Agent Environment</h2>
        </div>
        <div className="env-header-meta">
          <span><Box size={12} /> {overview?.home || '—'}</span>
          <span>python: {overview?.python_version || '—'}</span>
          <span>uv: {overview?.uv_version || '—'}</span>
          <span><HardDrive size={12} /> {fmtBytes(overview?.disk_bytes)}</span>
          <button className="env-btn ghost" onClick={() => refreshOverview(true)} title="Refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {overview && overview.disk_limit_bytes > 0 && (() => {
        const pct = Math.min(100, (overview.disk_bytes / overview.disk_limit_bytes) * 100);
        const isWarn = overview.disk_bytes >= overview.disk_warn_bytes;
        const isDanger = pct >= 95;
        const cls = isDanger ? 'danger' : isWarn ? 'warn' : '';
        return (
          <div className="env-storage-bar-wrap">
            <span className={`env-storage-label ${cls}`}>
              {fmtBytes(overview.disk_bytes)} / {fmtBytes(overview.disk_limit_bytes)}
              {isWarn ? (isDanger ? ' — almost full' : ' — approaching limit') : ''}
            </span>
            <div className="env-storage-track">
              <div className={`env-storage-fill ${cls}`} style={{ width: `${pct}%` }} />
            </div>
            <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{pct.toFixed(1)}%</span>
          </div>
        );
      })()}

      <div className="env-blurb">
        This is your agent's home — a real, persistent filesystem and Python environment that
        lives entirely on its own. Anything the agent writes here (notes, scratch files, cloned
        repos, edited skills, installed packages) sticks around across conversations and
        restarts. Browse it, edit it, install or remove packages — it's the agent's workspace
        to do whatever it needs to do.
      </div>

      {error && <div className="env-error">{error}</div>}

      <div className="env-body">
        <aside className="env-tree">
          <div className="env-tree-toolbar">
            <button className="env-btn small" onClick={newFile} disabled={!!busy}>
              <Plus size={12} /> File
            </button>
            <button className="env-btn small" onClick={newFolder} disabled={!!busy}>
              <Plus size={12} /> Folder
            </button>
            <button
              className="env-btn small danger"
              onClick={removePath}
              disabled={!!busy || !selected.path}
              title="Delete selected"
            >
              <Trash2 size={12} />
            </button>
          </div>
          <div className="env-tree-scroll">
            <TreeNode
              agentId={agentId}
              path=""
              name="~"
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              selected={selected.path}
              depth={0}
              refreshNonce={treeNonce}
            />
          </div>
        </aside>

        <section className="env-main">
          {showPackages ? (
            <div className="env-packages">
              <div className="env-packages-header">
                <h3><Package size={14} /> Installed packages</h3>
                <div className="env-packages-actions">
                  <input
                    className="env-input"
                    placeholder="package name (e.g. requests==2.31.0)"
                    value={pkgName}
                    onChange={e => setPkgName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && installPkg()}
                  />
                  <button className="env-btn" onClick={installPkg} disabled={busy === 'install' || !pkgName.trim()}>
                    {busy === 'install' ? 'Installing…' : 'Install'}
                  </button>
                  <button className="env-btn danger" onClick={rebuild} disabled={busy === 'rebuild'}>
                    {busy === 'rebuild' ? 'Rebuilding…' : 'Rebuild venv'}
                  </button>
                </div>
              </div>
              <div className="env-packages-list">
                {!overview?.packages?.length && <div className="env-empty">No packages installed yet.</div>}
                {overview?.packages?.map(p => (
                  <div key={p.name} className="env-package-row">
                    <span className="pkg-name">{p.name}</span>
                    <span className="pkg-ver">{p.version}</span>
                    <button
                      className="env-btn ghost small"
                      onClick={() => uninstallPkg(p.name)}
                      disabled={busy === `uninstall:${p.name}`}
                    >
                      <Trash2 size={11} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : selected.kind === 'file' ? (
            <div className="env-editor-wrap">
              <div className="env-editor-bar">
                <span className="env-editor-path">{selected.path}</span>
                <span className="env-editor-meta">
                  {fileBody ? `${fmtBytes(fileBody.size)} · ${fileBody.is_text ? 'text' : 'binary'}` : ''}
                </span>
                <button
                  className="env-btn"
                  onClick={save}
                  disabled={!dirty || busy === 'save' || !fileBody?.is_text}
                >
                  <Save size={12} /> {busy === 'save' ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
                </button>
              </div>
              {fileBody?.is_text === false ? (
                <div className="env-empty">Binary file — preview not available.</div>
              ) : (
                <div className="env-editor">
                  <Editor
                    height="100%"
                    theme="vs-dark"
                    language={langForPath(selected.path)}
                    value={editorValue}
                    onChange={(v) => { setEditorValue(v ?? ''); setDirty(true); }}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      automaticLayout: true,
                      wordWrap: 'on',
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="env-empty-pane">
              <Box size={36} />
              <p>This is your agent's home directory.</p>
              <p className="muted">
                Pick a file in the tree to view or edit it. Open <code>.venv</code> to manage Python packages.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
